import { Component } from '@theme/component';

/** One responsive background video, with no downloads until playback is needed. */
class CinematicHero extends Component {
  /** @type {AbortController | undefined} */
  #controller;
  /** @type {IntersectionObserver | undefined} */
  #observer;
  /** @type {HTMLVideoElement | null} */
  #video = null;
  /** @type {HTMLButtonElement | null} */
  #toggle = null;
  #visible = false;
  #userPaused = false;
  #manualPlay = false;
  #failed = false;
  #source = '';
  #motion = window.matchMedia('(prefers-reduced-motion: reduce)');
  /** @type {MediaQueryList[]} */
  #breakpoints = [];
  /** @type {Set<number>} */
  #frames = new Set();

  connectedCallback() {
    super.connectedCallback();
    this.#initialize();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#cleanup();
    this.#initialize();
  }

  #initialize() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const video = this.querySelector('[data-tb-video]');
    const toggle = this.querySelector('[data-tb-videotoggle]');
    this.#video = video instanceof HTMLVideoElement ? video : null;
    this.#toggle = toggle instanceof HTMLButtonElement ? toggle : null;
    this.#source = '';
    this.#failed = false;
    this.#visible = false;
    this.#manualPlay = false;
    this.#userPaused = this.dataset.autoplay !== 'true' || this.#motion.matches || navigator.connection?.saveData === true;
    this.#breakpoints = [
      window.matchMedia(`(max-width: ${this.dataset.mobileBreakpoint}px)`),
      window.matchMedia(`(max-width: ${this.dataset.tabletBreakpoint}px)`),
    ];
    for (const breakpoint of this.#breakpoints) breakpoint.addEventListener('change', () => this.#updateVideo(), { signal });
    this.#motion.addEventListener('change', () => {
      if (this.#motion.matches) {
        this.#userPaused = true;
        this.#manualPlay = false;
      }
      this.#updateVideo();
    }, { signal });
    document.addEventListener('visibilitychange', () => this.#updateVideo(), { signal });
    if (!this.classList.contains('tb-hero--lightweight') && !this.#motion.matches) this.#enableEffects(signal);

    if (this.#video) {
      this.#video.muted = true;
      this.#toggle?.removeAttribute('hidden');
      this.#toggle?.addEventListener('click', () => {
        if (this.#video?.paused) {
          this.#userPaused = false;
          this.#manualPlay = true;
          if (this.#failed) this.#source = '';
          this.#failed = false;
        } else {
          this.#userPaused = true;
        }
        this.#updateVideo();
      }, { signal });
      for (const event of ['playing', 'pause', 'error', 'emptied']) {
        this.#video.addEventListener(event, () => {
          if (event === 'error') this.#failed = true;
          if (event === 'playing') this.classList.add('is-video-playing');
          if (event === 'error' || event === 'emptied') this.classList.remove('is-video-playing');
          this.#syncToggle();
        }, { signal });
      }
      this.#syncToggle();
    }

    if ('IntersectionObserver' in window) {
      this.#observer = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (entry.target === this) {
            this.#visible = entry.isIntersecting;
            this.#updateVideo();
          } else if (entry.isIntersecting && entry.target instanceof HTMLElement) {
            this.#animateCounter(entry.target);
            this.#observer?.unobserve(entry.target);
          }
        }
      }, { threshold: 0.1 });
      this.#observer.observe(this);
      for (const counter of this.querySelectorAll('[data-tb-count]')) this.#observer.observe(counter);
    } else {
      this.#visible = true;
      this.#updateVideo();
    }
  }

  /** Legacy motion remains opt-in through the section's performance setting.
   * @param {AbortSignal} signal
   */
  #enableEffects(signal) {
    let pending = false;
    const update = () => {
      if (pending || this.#motion.matches) return;
      pending = true;
      this.#requestFrame(() => {
        pending = false;
        const bounds = this.getBoundingClientRect();
        if (bounds.bottom < 0 || bounds.top > innerHeight) return;
        const progress = (bounds.top + bounds.height / 2 - innerHeight / 2) / innerHeight;
        for (const element of this.querySelectorAll('[data-tb-parallax]')) {
          if (element instanceof HTMLElement) element.style.transform = `translateY(${progress * Number(element.dataset.tbParallax) * 160}px)`;
        }
      });
    };
    window.addEventListener('scroll', update, { signal, passive: true });
    window.addEventListener('resize', update, { signal });
    if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      this.addEventListener('pointermove', (event) => {
        if (this.#motion.matches || !(event instanceof PointerEvent)) return;
        const bounds = this.getBoundingClientRect();
        this.style.setProperty('--tb-mx', `${50 + ((event.clientX - bounds.left) / bounds.width - 0.5) * 34}%`);
        this.style.setProperty('--tb-my', `${46 + ((event.clientY - bounds.top) / bounds.height - 0.5) * 26}%`);
        this.style.setProperty('--tb-px', `${event.clientX - bounds.left}px`);
        this.style.setProperty('--tb-py', `${event.clientY - bounds.top}px`);
        this.classList.add('is-aimed');
      }, { signal, passive: true });
      this.addEventListener('pointerleave', () => this.classList.remove('is-aimed'), { signal });
    }
    update();
  }

  #updateVideo() {
    const video = this.#video;
    if (!video) return;
    const desktop = this.dataset.videoDesktop || '';
    const tablet = this.dataset.videoTablet || desktop;
    const source = this.#breakpoints[0]?.matches ? this.dataset.videoMobile || tablet : this.#breakpoints[1]?.matches ? tablet : desktop;
    if (this.#toggle) this.#toggle.hidden = !source;
    if (!source || !this.#visible || document.hidden || this.#userPaused) {
      video.pause();
      // Discard the previous crop when resizing while paused; keep the matching poster.
      if (source !== this.#source && this.#source) {
        video.removeAttribute('src');
        video.load();
        this.#source = '';
      }
      this.#syncToggle();
      return;
    }
    if (source !== this.#source) {
      video.pause();
      this.classList.remove('is-video-playing');
      this.#source = source;
      this.#failed = false;
      video.src = source;
      video.load();
    }
    if (this.#failed || (this.#motion.matches && !this.#manualPlay)) return;
    if (video.paused) {
      const attempt = video.play();
      attempt?.catch(() => {
        if (this.#source !== source) return;
        this.#syncToggle();
      });
    }
  }

  #syncToggle() {
    if (!this.#toggle || !this.#video) return;
    const paused = this.#video.paused || this.#failed;
    this.#toggle.classList.toggle('is-paused', paused);
    this.#toggle.setAttribute('aria-label', (paused ? this.dataset.playLabel : this.dataset.pauseLabel) || '');
  }

  /** @param {HTMLElement} element */
  #animateCounter(element) {
    const target = Number(element.dataset.tbCount);
    if (!Number.isFinite(target) || this.#motion.matches) return;
    const suffix = element.dataset.tbSuffix || '';
    const duration = Number(this.dataset.counterDuration) || 1500;
    const start = performance.now();
    /** @param {number} time */
    const frame = (time) => {
      const progress = Math.min((time - start) / duration, 1);
      element.textContent = Math.round(target * (1 - (1 - progress) ** 3)) + suffix;
      if (progress < 1) this.#requestFrame(frame);
    };
    this.#requestFrame(frame);
  }

  /** @param {FrameRequestCallback} callback */
  #requestFrame(callback) {
    const id = requestAnimationFrame((time) => {
      this.#frames.delete(id);
      callback(time);
    });
    this.#frames.add(id);
  }

  #cleanup() {
    this.#controller?.abort();
    this.#observer?.disconnect();
    for (const frame of this.#frames) cancelAnimationFrame(frame);
    this.#frames.clear();
    this.#video?.pause();
    this.classList.remove('is-video-playing');
    this.classList.remove('is-aimed');
    for (const property of ['--tb-mx', '--tb-my', '--tb-px', '--tb-py']) this.style.removeProperty(property);
    for (const element of this.querySelectorAll('[data-tb-parallax]')) {
      if (element instanceof HTMLElement) element.style.removeProperty('transform');
    }
  }

  disconnectedCallback() {
    this.#cleanup();
    super.disconnectedCallback();
  }
}

if (!customElements.get('cinematic-hero')) customElements.define('cinematic-hero', CinematicHero);
